import { AxiosResponse } from 'axios';

import i18n from '../i18n/i18n';
import API from '../utils/API';
import { axiosInstance, casinoApi, svaraApi } from '../utils/axiosInstance';
import { env } from '../utils/env';
import LS from '../utils/localStorageUtils';
import { clearPendingInviteCode, getPendingInviteCode } from './invite/pendingInviteCode';

export function useAPIAuthRequest() {
	async function AuthRequestToApi(endpoint, method, data) {
		return API.authRequest(endpoint, method, data, {
			'Content-Type': 'application/json',
			...API.getXCasinoHeader(),
		});
	}
	return { AuthRequestToApi };
}

export async function getUserProfile() {
	try {
		const { data } = await svaraApi.get('/api/player/profile');
		return data.data;
	} catch (err: any) {
		if (err?.response?.status === 401) {
			console.log('[API] User is unauthenticated (401)');
		} else {
			console.error('Failed to load player profile', err);
		}
		throw err;
	}
}

export type TournamentStatisticsBreakdown = {
	'1': number;
	'2': number;
	'3': number;
	total: number;
};

export type MyTournamentStatistics = {
	gold: TournamentStatisticsBreakdown;
	silver: TournamentStatisticsBreakdown;
	bronze: TournamentStatisticsBreakdown;
};

// Response shape example:
// { name: "OK", status: 200, data: { gold: { "1":0,"2":0,"3":0,total:0 }, ... } }
export async function getMyTournamentStatistics() {
	try {
		const { data } = await svaraApi.get('/api/player/tournaments/my_statistics');
		return data.data as MyTournamentStatistics;
	} catch (err: any) {
		if (err?.response?.status === 401) {
			console.log('[API] Tournament statistics not available for unauthenticated user (401)');
		} else {
			console.error('Failed to load my tournament statistics', err);
		}
		throw err;
	}
}

export async function getPlayerTournamentStatistics(playerId: string) {
	try {
		const { data } = await svaraApi.get(`/api/player/tournaments/statistics/${playerId}`);
		return data.data as MyTournamentStatistics;
	} catch (err: any) {
		if (err?.response?.status === 401) {
			console.log('[API] Tournament statistics not available for this player (401)');
		} else {
			console.error('Failed to load player tournament statistics', err);
		}
		throw err;
	}
}

/**
 * `isVip` narrows the list to the VIP room (true) or to every room but it (false). A backend
 * without the filter rejects the unknown param with a 400; the call is then repeated without it
 * and returns all rooms, so callers still filter on `room.isVip`.
 */
export async function getLobbyRooms(params: {
	start: number;
	limit: number;
	isVip?: boolean;
}): Promise<AxiosResponse> {
	const page = { _start: params.start, _end: params.start + params.limit };
	try {
		if (params.isVip !== undefined) {
			try {
				return await svaraApi.get('/api/lobby/rooms', {
					params: { ...page, isVip: params.isVip },
				});
			} catch (err: any) {
				if (err.response?.status !== 400) throw err;
			}
		}
		return await svaraApi.get('/api/lobby/rooms', { params: page });
	} catch (err: any) {
		if (err.response?.status === 401) {
			return Promise.reject({
				...err,
				skipNavigation: true,
			});
		}
		throw err;
	}
}

export async function createSpecialTable(data: {
	playerId: string;
	stake: number;
	minimalAmount: number;
	maximalAmount: number;
	maxSeats: number;
	turnDuration: number;
	isPrivate: boolean;
}) {
	try {
		const response = await svaraApi.post('/api/lobby/tables/special', data);
		console.log('response data from VIP table', response.data);
		return response.data;
	} catch (err: any) {
		console.error('[createSpecialTable] error:', err);
		throw err;
	}
}

export async function unlockRoomPurchase(data: { roomId: string }) {
	try {
		const response = await svaraApi.post('/api/lobby/purchase/room', data);
		return response.data;
	} catch (err: any) {
		console.error('[unlockRoomPurchase] error:', err);
		throw err;
	}
}

export async function getMyActiveTables() {
	try {
		const response = await svaraApi.get('/api/lobby/tables/my_active_tables');

		return response;
	} catch (err: any) {
		if (err?.response?.status === 401) {
			console.log('[API] Active tables not available for unauthenticated user (401)');
		} else {
			console.error('Failed to load my tables', err);
		}
		throw err;
	}
}

export async function getActivePlayers(params: {
	start: number;
	limit: number;
	tableId?: string | number;
}) {
	try {
		const response = await svaraApi.get('/api/player/players/active', {
			params: {
				_start: params.start,
				_end: params.start + params.limit,
				tableId: params.tableId,
			},
		});
		return response;
	} catch (err: any) {
		if (err.response?.status === 401) {
			return Promise.reject({
				...err,
				skipNavigation: true,
			});
		}
		throw err;
	}
}

export async function refreshOnlinePresence() {
	await svaraApi.post('/api/player/setOnlineStatus');
}

export async function clearOnlinePresence() {
	await svaraApi.post('/api/player/setOnlineStatus', { online: false });
}

export async function getActiveUsersCounter() {
	try {
		const response = await casinoApi.get('/api/lobby/users/active/counter');
		console.log('active users count', response.data);
		return response.data;
	} catch (err: any) {
		throw err;
	}
}

export async function getUserStatistics(playerId?: string) {
	try {
		const userId =
			playerId ||
			(() => {
				const userStr = LS.get('auth_user');
				const user = userStr ? JSON.parse(userStr) : null;
				return user?.id;
			})();

		const response = await axiosInstance.get(`/api/player/statistics/${userId}`, {
			params: { gameType: 'SVARA' },
		});
		return response;
	} catch (err: any) {
		if (err.response?.status === 401) {
			return Promise.reject({
				...err,
				skipNavigation: true,
			});
		}
		throw err;
	}
}

export async function collectDeltaDNA(endpoint: string, data?: any) {
	try {
		const response = await svaraApi.post(`/api/deltadna/collect/${endpoint}`, data || {});
		console.log('collect delta dna', response.data);
		return response.data;
	} catch (err: any) {
		console.error('[Collect DELTA DNA] error:', err);
		throw err;
	}
}

export async function engageDeltaDNA(endpoint: string) {
	try {
		const response = await svaraApi.get(`/api/deltadna/engage/${endpoint}`);
		console.log('engage delta dna GET', response.data);
		return response.data;
	} catch (err: any) {
		console.error('[Engage DELTA DNA GET] error:', err);
		throw err;
	}
}

export async function createPlayerReport(data: {
	reporterId: string;
	reportedId: string;
	reportType: string;
	reason: string;
	gameContext: string;
}) {
	try {
		const response = await svaraApi.post('/api/player/reports', data);
		return response.data;
	} catch (err: any) {
		console.error('[createPlayerReport] error:', err);
		throw err;
	}
}

export async function getPackages(params: { start: number; limit: number }) {
	try {
		const response = await axiosInstance.get('/api/store/packages', {
			params: {
				_start: params.start,
				_end: params.start + params.limit,
				gameType: 'SVARA',
			},
		});
		return response;
	} catch (err: any) {
		if (err.response?.status === 401) {
			return Promise.reject({
				...err,
				skipNavigation: true,
			});
		}
		throw err;
	}
}

export async function getPackageById(id: string) {
	try {
		const response = await axiosInstance.get(`/api/store/package/${id}`);
		return response;
	} catch (err: any) {
		if (err.response?.status === 401) {
			return Promise.reject({
				...err,
				skipNavigation: true,
			});
		}
		throw err;
	}
}

/**
 * The players service answers a rejected update (a third non-VIP username change, a taken name)
 * by returning the exception rather than throwing it, so it can arrive inside a 2xx body. This
 * pulls the message out of such a body, or returns undefined for a real success.
 */
const rejectedUpdateMessage = (body: any): string | undefined => {
	for (const candidate of [body, body?.data, body?.value, body?.data?.value]) {
		const status = Number(candidate?.status ?? candidate?.statusCode);
		if (!(status >= 400)) continue;
		const message = candidate?.response?.message ?? candidate?.message;
		if (Array.isArray(message)) return String(message[0]);
		return typeof message === 'string' && message
			? message
			: i18n.t('T_SITE_PROFILE_UPDATE_FAILED', { defaultValue: 'Update profile failed' });
	}
	return undefined;
};

export type UpdateProfileResult = {
	/** A notice the server attached to an accepted update, e.g. that an avatar is held for review. */
	notice?: string;
};

export async function updateProfile(profileData: any): Promise<UpdateProfileResult> {
	try {
		const response = await svaraApi.patch('/api/player/profile', profileData, {
			headers: {
				'X-Trinity-Update-Remarks': 'profile-update',
			},
		});
		const rejected = rejectedUpdateMessage(response.data);
		if (rejected) throw new Error(rejected);
		const body = response.data?.data ?? response.data;
		const notice = typeof body?.message === 'string' && body.message ? body.message : undefined;
		return { notice };
	} catch (err: any) {
		console.error('[updateProfile] error:', err);
		throw err;
	}
}

export async function getBonus() {
	try {
		const response = await svaraApi.get('/api/lobby/bonus/periodic');

		return response;
	} catch (err: any) {
		console.error('[bonus] error:', err);
		throw err;
	}
}

export async function collectBonus() {
	try {
		const response = await svaraApi.patch('/api/lobby/bonus/periodic');
		return response;
	} catch (err: any) {
		console.error('[bonus] error:', err);
		throw err;
	}
}

export async function getMiniGames() {
	try {
		const response = await svaraApi.get('/api/game/games');
		return response.data;
	} catch (err: any) {
		console.error('[miniGames] error:', err);
		throw err;
	}
}

export async function getMiniGamesRewards() {
	try {
		const response = await svaraApi.get('/api/game/games/rewards');
		return response.data;
	} catch (err: any) {
		console.error('[miniGamesRewards] error:', err);
		throw err;
	}
}

export async function getJackpotHistory() {
	try {
		const response = await svaraApi.get('/api/game/games/jackpot_history');
		return response.data;
	} catch (err: any) {
		console.error('[jackpotHistory] error:', err);
		throw err;
	}
}

export async function getNotifications(
	playerId: string,
	params?: {
		_start?: number;
		_end?: number;
		search?: string;
	},
) {
	const query = {
		_start: typeof params?._start === 'number' ? params._start : 0,
		_end: typeof params?._end === 'number' ? params._end : 50,
		...(params?.search ? { search: params.search } : {}),
	};
	const response = await svaraApi.get(`/api/notifications/${playerId}`, {
		params: query,
	});

	return response.data;
}

export async function deleteNotificationRead(notificationId: string) {
	const { data } = await svaraApi.delete(`/api/notifications/${notificationId}`);
	return data;
}

export async function patchNotificationSeen(
	notificationId: string,
	playerId: string,
	isMassSeen?: boolean,
) {
	const { data } = await svaraApi.patch(`/api/notifications/${notificationId}/${playerId}`, {
		isSeen: true,
		isMassSeen: isMassSeen ?? false,
	});
	return data;
}

export async function fetchTranslations(langId: number) {
	try {
		const response = await svaraApi.get(`/api/svara/translations/${langId}`);

		return response.data.data;
	} catch (err: any) {
		console.error('[translation] error:', err);
		throw err;
	}
}

export async function sendFacebookInvitations(recipientIds: string[]) {
	if (!recipientIds?.length) return [];

	try {
		const responses = await Promise.all(
			recipientIds.map(facebook_id =>
				svaraApi.post('/api/svara/facebook/invitation', { facebook_id }),
			),
		);

		return responses.map(r => r.data);
	} catch (err: any) {
		console.error('[invite] error sending invitations:', err);
		throw err;
	}
}

export async function fetchGeneralRankings(params?: {
	_start?: number;
	_end?: number;
	search?: string;
}) {
	return svaraApi.get('/api/svara/rankings/general', { params });
}

export type SvaraMissionEventType =
	| 'HandPlayed'
	| 'HandWon'
	| 'GameWon'
	| 'PotWon'
	| 'BetPlaced'
	| 'Raise'
	| 'Call'
	| 'BlindBet'
	| 'SvarkaPlayed'
	| 'SvarkaWon'
	| 'HandCombination'
	| 'PointsScored'
	| 'ChipsEarned'
	| 'DuelPlayed'
	| 'DuelWon'
	| 'MiniGamePlayed'
	| 'GiftSent'
	| 'DailyLogin';

export type SvaraHandCombination =
	| 'SevenClubs'
	| 'ThreeSevens'
	| 'ThreeOfAKind'
	| 'ThreeSameSuit'
	| 'TwoSevens'
	| 'TwoAces'
	| 'TwoSameSuit'
	| 'HighCard';

export type SvaraMissionRewardDto = {
	rewardType: 'Chips' | 'Experience';
	amount: number;
};

export type SvaraDailyMissionDto = {
	id: string;
	title: string;
	description: string | null;
	eventType: SvaraMissionEventType;
	progressMode: 'Count' | 'Accumulate';
	targetCombination: SvaraHandCombination | null;
	minStake: number | null;
	completionCount: number;
	currentCount: number;
	completed: boolean;
	claimed: boolean;
	thumbnailId: string | null;
	thumbnailMobileId: string | null;
	rewards: SvaraMissionRewardDto[];
};

export type SvaraDailyCompletionDto = {
	available: boolean;
	completed: boolean;
	claimed: boolean;
	rewards: SvaraMissionRewardDto[];
};

export type SvaraDailyMissionsResponse = {
	cycleKey: string;
	resetAt: string;
	missions: SvaraDailyMissionDto[];
	completion: SvaraDailyCompletionDto;
};

export type SvaraMissionClaimGrant = {
	id: string;
	rewardType: 'Chips' | 'Experience';
	amount: number;
	status: 'Pending' | 'Paid' | 'Failed';
	paidTransactionId: string | null;
};

export type SvaraMissionClaimResponse = {
	outcome: 'paid' | 'partial' | 'alreadyClaimed';
	grants: SvaraMissionClaimGrant[];
};

const MISSION_REQUEST_TIMEOUT_MS = 30000;

export async function getDailyMissions(): Promise<SvaraDailyMissionsResponse> {
	const response = await svaraApi.get('/api/game/missions/daily', {
		timeout: MISSION_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export async function claimMissionReward(
	missionId: string,
	cycleKey: string,
): Promise<SvaraMissionClaimResponse> {
	const response = await svaraApi.post(
		`/api/game/missions/${missionId}/claim`,
		{ cycleKey },
		{ timeout: MISSION_REQUEST_TIMEOUT_MS },
	);
	return response.data;
}

export async function claimDailyMissionsChest(
	cycleKey: string,
): Promise<SvaraMissionClaimResponse> {
	const response = await svaraApi.post(
		'/api/game/missions/daily/completion/claim',
		{ cycleKey },
		{ timeout: MISSION_REQUEST_TIMEOUT_MS },
	);
	return response.data;
}

/** gateway-game `/api/support/contact` - Help & Contacts form, lands in the back-office Support inbox. */
export async function sendContactMessage(payload: {
	name: string;
	email: string;
	message: string;
}): Promise<{ id: string }> {
	const response = await svaraApi.post('/api/support/contact', payload);
	return response.data;
}

/** gateway-game `/api/friends` - the Svara friends graph (`PlayerFriendRequest`). */
export type PlayersDirectoryFilter = 'ALL' | 'FRIENDS' | 'ONLINE' | 'VIP';

export type PlayerFriendshipStatus = 'NONE' | 'PENDING_OUTGOING' | 'PENDING_INCOMING' | 'FRIENDS';

export type PlayersDirectoryItem = {
	id: string;
	username: string | null;
	avatarFileId: string | null;
	level: number;
	isVip: boolean;
	isOnline: boolean;
	friendshipStatus: PlayerFriendshipStatus;
	/** Set while a request is pending in either direction. */
	friendRequestId: string | null;
};

export type PlayersDirectoryResponse = {
	totalCount: number;
	items: PlayersDirectoryItem[];
};

export type PendingFriendRequest = {
	id: string;
	requesterId: string;
	requesterUsername: string | null;
	requesterAvatarFileId: string | null;
	requesterLevel: number;
	requesterIsVip: boolean;
	createdAt: string;
};

export type PendingFriendRequestsResponse = {
	totalCount: number;
	items: PendingFriendRequest[];
};

/** The Social screen's filter popup, applied by the server. Every bound is inclusive. */
export type PlayersDirectoryProfileFilter = {
	gender?: 'Male' | 'Female';
	minAge?: number;
	maxAge?: number;
	minLevel?: number;
	maxLevel?: number;
	location?: string;
};

/** The server caps a page at 100 rows. */
export const FRIENDS_PAGE_SIZE = 100;

export async function getPlayersDirectory(params: {
	filter: PlayersDirectoryFilter;
	query?: string;
	start?: number;
	profile?: PlayersDirectoryProfileFilter;
}): Promise<PlayersDirectoryResponse> {
	const start = params.start ?? 0;
	const response = await svaraApi.get('/api/friends/players', {
		params: {
			_start: start,
			_end: start + FRIENDS_PAGE_SIZE,
			filter: params.filter,
			...(params.query ? { query: params.query } : {}),
			...params.profile,
		},
	});
	return response.data;
}

export async function getPendingFriendRequests(): Promise<PendingFriendRequestsResponse> {
	const response = await svaraApi.get('/api/friends/requests/pending', {
		params: { _start: 0, _end: FRIENDS_PAGE_SIZE },
	});
	return response.data;
}

/** `status` is ACCEPTED when the other player had already asked, so the two requests met. */
export async function sendFriendRequest(
	addresseeId: string,
): Promise<{ id: string; status: 'PENDING' | 'ACCEPTED' }> {
	const response = await svaraApi.post('/api/friends/requests', { addresseeId });
	return response.data;
}

export async function respondToFriendRequest(
	requestId: string,
	status: 'ACCEPTED' | 'REJECTED',
): Promise<{ id: string; status: string }> {
	const response = await svaraApi.patch(`/api/friends/requests/${requestId}`, { status });
	return response.data;
}

export async function removeFriend(friendId: string): Promise<{ id: string }> {
	const response = await svaraApi.delete(`/api/friends/${friendId}`);
	return response.data;
}

export const SVARA_ACHIEVEMENT_CATEGORIES = [
	'Gameplay',
	'Duels',
	'Events',
	'Arcade',
	'Social',
	'Progression',
	'Loyalty',
] as const;

export type SvaraAchievementCategory = (typeof SVARA_ACHIEVEMENT_CATEGORIES)[number];

export type SvaraAchievementMetric =
	| 'HandsPlayed'
	| 'HandsWon'
	| 'MaxPotWon'
	| 'ThreeSevens'
	| 'SvarkaWon'
	| 'DuelsWon'
	| 'EventsJoined'
	| 'EventTracksCompleted'
	| 'ArcadeRounds'
	| 'Friends'
	| 'GiftsSent'
	| 'GiftsReceived'
	| 'ProfileComplete'
	| 'Level'
	| 'ActiveDays'
	| 'Purchases'
	/** Donations made to other players (family DONATIONS_SENT, "Generous Giver"). */
	| 'DonationsSent';

export type SvaraAchievementTierDto = {
	key: string;
	displayTier: number;
	nameKey: string;
	descriptionKey: string;
	target: number;
	iconKey: string;
	iconFileId: string | null;
	unlockedAt: string | null;
};

export type SvaraAchievementFamilyDto = {
	familyKey: string;
	nameKey: string;
	taglineKey: string;
	category: SvaraAchievementCategory;
	categoryNameKey: string;
	metric: SvaraAchievementMetric;
	descriptionKey: string;
	progress: number;
	currentTier: SvaraAchievementTierDto | null;
	nextTier: SvaraAchievementTierDto | null;
	tiers: SvaraAchievementTierDto[];
	featured: boolean;
};

export type SvaraPlayerAchievementsResponse = {
	families: SvaraAchievementFamilyDto[];
	unlockedCount: number;
	totalCount: number;
	featuredFamilyKeys: string[];
	maxFeatured: number;
	showcaseVersion: number;
};

export type SvaraAchievementShowcaseResponse = {
	featuredFamilyKeys: string[];
	version: number;
};

export type SvaraFeaturedAchievementDto = {
	familyKey: string;
	category: SvaraAchievementCategory;
	key: string;
	displayTier: number;
	nameKey: string;
	iconKey: string;
	iconFileId: string | null;
};

export type SvaraFeaturedAchievementsResponse = {
	playerId: string;
	featured: SvaraFeaturedAchievementDto[];
};

const ACHIEVEMENT_REQUEST_TIMEOUT_MS = 30000;

export async function getPlayerAchievements(): Promise<SvaraPlayerAchievementsResponse> {
	const response = await svaraApi.get('/api/game/achievements', {
		timeout: ACHIEVEMENT_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export async function getFeaturedAchievements(
	playerId: string,
): Promise<SvaraFeaturedAchievementsResponse> {
	const response = await svaraApi.get(
		`/api/game/achievements/players/${encodeURIComponent(playerId)}/featured`,
		{ timeout: ACHIEVEMENT_REQUEST_TIMEOUT_MS },
	);
	return response.data;
}

export async function updateFeaturedAchievements(
	familyKeys: string[],
	expectedVersion: number,
): Promise<SvaraAchievementShowcaseResponse> {
	const response = await svaraApi.put(
		'/api/game/achievements/featured',
		{ familyKeys, expectedVersion },
		{ timeout: ACHIEVEMENT_REQUEST_TIMEOUT_MS },
	);
	return response.data;
}

export type DuelDivisionDto = { name: string; tier: number | null; label: string };

export type DuelPlayerRatingDto = {
	gameType: string;
	rating: number;
	duelsRated: number;
	peakRating: number;
	division: DuelDivisionDto;
	nextDivision: { name: string; minRating: number } | null;
	recent: {
		duelId: string;
		before: number;
		after: number;
		delta: number;
		divisionBefore: string;
		divisionAfter: string;
		divisionChange: 'promoted' | 'demoted' | null;
		at: string;
	}[];
};

export type DuelSeasonSummaryDto = {
	id: string;
	name: string;
	startsAt: string;
	endsAt: string;
	status: string;
};

export type DuelSeasonMilestoneDto = {
	id: string;
	threshold: number;
	rewardType: DuelRewardType;
	value: number;
	quantity: number;
	title: string | null;
	reached: boolean;
	entitlementStatus: string | null;
};

export type DuelSnapshotDto = {
	id: string;
	currentPlayers: number;
	maxPlayers: number;
	gameType: string;
	duelTypeId: string | null;
	duelTypeName: string | null;
	joinClosesAt: string | null;
	currentPlayerDeadlineAt?: string | null;
	status: 'open' | 'active' | 'completed';
	players: string[];
	currentPlayerId: string | null;
	currentPlayerTableId: string | null;
	currentPlayerFinished: boolean;
	ranked?: boolean;
	eligibleCount?: number;
	participantCount?: number;
	finishedCount?: number;
	currentPlayerRank?: number | null;
	currentPlayerWon?: boolean;
	currentPlayerDisplayScore?: number | null;
	provisional?: boolean;
	settledAt?: string | null;
	currentPlayerRatingAtJoin?: number | null;
	currentPlayerComputedRatingDelta?: number | null;
	currentPlayerSeasonPoints?: number | null;
	endReason: string | null;
	expiresAt: string | null;
};

export type DuelHubPoolDto = {
	duelTypeId: string;
	duelTypeName: string;
	ranked: boolean;
	available: boolean;
	openPool: {
		duelId: string;
		players: number;
		maxPlayers: number;
		joinClosesAt: string | null;
	} | null;
};

export type DuelHubResponse = {
	rating: DuelPlayerRatingDto;
	season: {
		season: DuelSeasonSummaryDto | null;
		points: number;
		position: number | null;
		nextMilestone: DuelSeasonMilestoneDto | null;
	};
	rewards: {
		requestable: number;
		openRequests: number;
		recentlyFulfilled: number;
		claimable?: number;
	};
	currentDuel: DuelSnapshotDto | null;
	pools: DuelHubPoolDto[];
};

export type DuelPlacementRewardDto = {
	chips: number | null;
	experience: number | null;
	chipsStatus: string | null;
	experienceStatus: string | null;
};

export type DuelHistoryEntryDto = {
	duelId: string;
	duelTypeId: string | null;
	duelTypeName: string | null;
	settledAt: string;
	rank: number | null;
	eligibleCount: number;
	eligible: boolean;
	won: boolean;
	ranked: boolean;
	displayScore: number;
	ratingBefore: number | null;
	ratingAfter: number | null;
	ratingDelta: number | null;
	divisionChange: string | null;
	divisionAfter: string | null;
	seasonPointsBefore: number | null;
	seasonPointsAfter: number | null;
	seasonPointsDelta: number | null;
	placementReward: DuelPlacementRewardDto | null;
	milestones: DuelResultMilestoneDto[];
};

export type DuelHistoryResponse = {
	items: DuelHistoryEntryDto[];
	nextCursor: string | null;
	personalBests: {
		duelTypeId: string | null;
		duelTypeName: string | null;
		displayScore: number;
	}[];
};

export type DuelHistoryQuery = {
	cursor?: string;
	limit?: number;
	seasonId?: string;
	duelTypeId?: string;
};

export type DuelResultBreakdownDto = {
	outcome: number;
	decisionQuality: number;
	riskManagement: number;
	timeBonus: number;
	total: number;
};

export type DuelResultLeaderboardEntryDto = {
	rank: number;
	playerId: string;
	username: string | null;
	avatarFileId: string | null;
	displayScore: number;
	isYou: boolean;
};

export type DuelResultMilestoneDto = {
	milestoneId: string;
	threshold: number | null;
	rewardType: string | null;
	value: number | null;
	quantity: number | null;
	title: string | null;
	status: string | null;
};

export type DuelResultSettlementDto = {
	ratingBefore: number | null;
	ratingAfter: number | null;
	ratingDelta: number | null;
	divisionBefore: string | null;
	divisionAfter: string | null;
	divisionChange: string | null;
	seasonPointsBefore: number | null;
	seasonPointsAfter: number | null;
	seasonPointsDelta: number | null;
	placementReward: DuelPlacementRewardDto | null;
	milestones: DuelResultMilestoneDto[];
};

export type DuelResultResponse = {
	duelId: string;
	duelTypeId: string | null;
	duelTypeName: string | null;
	status: string;
	provisional: boolean;
	ranked: boolean;
	eligibleCount: number;
	participantCount: number;
	finishedCount: number;
	expiresAt: string | null;
	settledAt: string | null;
	rank: number | null;
	displayScore: number | null;
	won: boolean;
	eligible: boolean;
	finished: boolean;
	personalBest: boolean;
	breakdown: DuelResultBreakdownDto | null;
	runDurationMs: number | null;
	handsPlayed: number | null;
	leaderboard: DuelResultLeaderboardEntryDto[];
	settlement: DuelResultSettlementDto | null;
};

export type DuelPlayerSeasonDto = {
	season: DuelSeasonSummaryDto;
	final: boolean;
	points: number;
	position: number | null;
};

const DUEL_REQUEST_TIMEOUT_MS = 30000;
const DUEL_GAME_TYPE = 'svara';

export async function getDuelHub(): Promise<DuelHubResponse> {
	const response = await svaraApi.get('/api/game/duels/hub', {
		params: { gameType: DUEL_GAME_TYPE },
		timeout: DUEL_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export async function getDuelHistory(query: DuelHistoryQuery): Promise<DuelHistoryResponse> {
	const response = await svaraApi.get('/api/game/duels/history', {
		params: { gameType: DUEL_GAME_TYPE, ...query },
		timeout: DUEL_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export async function getDuelResult(duelId: string): Promise<DuelResultResponse> {
	const response = await svaraApi.get(`/api/game/duels/${encodeURIComponent(duelId)}/result`, {
		params: { gameType: DUEL_GAME_TYPE },
		timeout: DUEL_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export async function getDuelSeasons(): Promise<DuelPlayerSeasonDto[]> {
	const response = await svaraApi.get('/api/game/duels/seasons', {
		params: { gameType: DUEL_GAME_TYPE },
		timeout: DUEL_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export type DuelRewardStatus =
	| 'Unlocked'
	| 'Claimed'
	| 'Granted'
	| 'GrantFailed'
	| 'Requested'
	| 'UnderReview'
	| 'Approved'
	| 'Rejected'
	| 'Fulfilled'
	| 'Failed'
	| 'Cancelled'
	| 'Expired';

export type DuelRewardType = 'Chips' | 'Experience' | 'VipDays' | 'RealWorld';

export type DuelRewardMethod = 'PayPal' | 'GiftCard' | 'Voucher';

export type DuelRewardHistoryEntryDto = {
	at: string;
	action: string;
	from: string | null;
	to: string | null;
	actorType: string;
	method?: DuelRewardMethod;
};

export type DuelPlayerRewardDto = {
	id: string;
	seasonId: string;
	seasonName: string;
	milestoneId: string;
	threshold: number;
	rewardType: DuelRewardType;
	value: number;
	quantity: number;
	title: string | null;
	status: DuelRewardStatus;
	unlockedAt: string;
	requestableUntil: string | null;
	canRequest: boolean;
	canCancel: boolean;
	canClaim: boolean;
	claimableUntil: string | null;
	history: DuelRewardHistoryEntryDto[];
};

export type DuelRewardTransitionDto = { id: string; status: DuelRewardStatus };

export type DuelSeasonProgressResponse = {
	season: DuelSeasonSummaryDto | null;
	points: number;
	position: number | null;
	milestones: DuelSeasonMilestoneDto[];
	nextMilestone: DuelSeasonMilestoneDto | null;
};

export type DuelSeasonLeaderboardEntryDto = {
	position: number;
	playerId: string;
	username: string | null;
	avatarFileId: string | null;
	points: number;
	rating: number | null;
	divisionLabel: string | null;
};

export type DuelSeasonLeaderboardResponse = {
	season: DuelSeasonSummaryDto | null;
	final: boolean;
	items: DuelSeasonLeaderboardEntryDto[];
	own: { position: number | null; points: number };
};

export type DuelPlayerSummaryDto = {
	playerId: string;
	username: string | null;
	rating: number;
	division: DuelDivisionDto;
	peakRating: number;
	duelsPlayed: number;
	wins: number;
};

export async function getDuelSeason(): Promise<DuelSeasonProgressResponse> {
	const response = await svaraApi.get('/api/game/duels/season', {
		params: { gameType: DUEL_GAME_TYPE },
		timeout: DUEL_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export async function getDuelRewards(): Promise<DuelPlayerRewardDto[]> {
	const response = await svaraApi.get('/api/game/duels/rewards', {
		timeout: DUEL_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export async function requestDuelReward(
	entitlementId: string,
	details: { method: DuelRewardMethod; email: string },
): Promise<DuelRewardTransitionDto> {
	const response = await svaraApi.post(
		`/api/game/duels/rewards/${encodeURIComponent(entitlementId)}/request`,
		details,
		{ timeout: DUEL_REQUEST_TIMEOUT_MS },
	);
	return response.data;
}

export async function claimDuelReward(entitlementId: string): Promise<DuelRewardTransitionDto> {
	const response = await svaraApi.post(
		`/api/game/duels/rewards/${encodeURIComponent(entitlementId)}/claim`,
		{},
		{ timeout: DUEL_REQUEST_TIMEOUT_MS },
	);
	return response.data;
}

export async function cancelDuelReward(entitlementId: string): Promise<DuelRewardTransitionDto> {
	const response = await svaraApi.post(
		`/api/game/duels/rewards/${encodeURIComponent(entitlementId)}/cancel`,
		{},
		{ timeout: DUEL_REQUEST_TIMEOUT_MS },
	);
	return response.data;
}

export async function getDuelSeasonRanking(season: string): Promise<DuelSeasonLeaderboardResponse> {
	const response = await svaraApi.get('/api/svara/rankings/duels', {
		params: { gameType: DUEL_GAME_TYPE, season },
		timeout: DUEL_REQUEST_TIMEOUT_MS,
	});
	return response.data;
}

export async function getDuelPlayerSummary(playerId: string): Promise<DuelPlayerSummaryDto> {
	const response = await svaraApi.get(
		`/api/game/duels/players/${encodeURIComponent(playerId)}/summary`,
		{ params: { gameType: DUEL_GAME_TYPE }, timeout: DUEL_REQUEST_TIMEOUT_MS },
	);
	return response.data;
}

// ---------------------------------------------------------------------------
// VIP tickets (trinity-nest gateway-game `vip-tickets`)
// ---------------------------------------------------------------------------

export type VipTicketWallet = {
	playerId: string;
	balance: number;
	lastDailyGrantDate?: string | null;
};

export async function getVipTicketWallet(): Promise<VipTicketWallet> {
	const response = await svaraApi.get('/api/vip-tickets');
	return response.data;
}

// ---------------------------------------------------------------------------
// Ad Reward
// ---------------------------------------------------------------------------

export async function getAdRewardStatus(): Promise<{
	hasPendingReward: boolean;
	chips: number;
	state: 'created' | 'approved' | null;
	cooldownMinutesLeft: number;
}> {
	const response = await svaraApi.get('/api/lobby/bonus/ad_reward');
	return response.data?.data;
}

export async function claimAdReward(): Promise<{
	chips: number;
	state: 'approved';
}> {
	const response = await svaraApi.patch('/api/lobby/bonus/ad_reward');
	return response.data?.data;
}

export function getRankingsByPeriod(params: {
	period: 'Daily' | 'Weekly' | 'Monthly';
	_start?: number;
	_end?: number;
}) {
	return svaraApi.get('/api/svara/rankings/byPeriod', { params });
}

export function getRankingsTournaments(params: { _start?: number; _end?: number }) {
	return svaraApi.get('/api/svara/rankings/tournaments', { params });
}

/**
 * A pending invite code rides along on social login: if this login creates the account, the backend
 * attributes it to the inviter; for an existing account it is ignored. Either way it is spent once
 * a sign-in succeeds - see `services/invite/pendingInviteCode`.
 */
async function inviteCodeField(): Promise<{ invitedBy?: string }> {
	const invitedBy = await getPendingInviteCode();
	return invitedBy ? { invitedBy } : {};
}

function clearInviteCodeAfterSignIn(data: any) {
	if (data?.access_token || data?.accessToken || data?.token) {
		clearPendingInviteCode();
	}
}

export async function socialLoginGoogle(idToken: string, confirmed?: boolean) {
	const casinoId = env('CASINO_ID');

	const requestBody = {
		idToken,
		socialPlatform: 'Google' as const,
		...(confirmed !== undefined ? { confirmed } : {}),
		...(await inviteCodeField()),
	};

	const res = await svaraApi.post('/api/svara/auth/socialLogin', requestBody, {
		headers: {
			'Content-Type': 'application/json',
			'x-casino-context': JSON.stringify({ casinoId }),
		},
	});

	clearInviteCodeAfterSignIn(res.data);
	return res.data;
}

export async function socialLoginApple(idToken: string, confirmed?: boolean) {
	const casinoId = env('CASINO_ID');

	const requestBody = {
		idToken,
		socialPlatform: 'AppleID' as const,
		...(confirmed !== undefined ? { confirmed } : {}),
		...(await inviteCodeField()),
	};

	const res = await svaraApi.post('/api/svara/auth/socialLogin', requestBody, {
		headers: {
			'Content-Type': 'application/json',
			'x-casino-context': JSON.stringify({ casinoId }),
		},
	});

	clearInviteCodeAfterSignIn(res.data);
	return res.data;
}

export async function socialLoginFacebook(accessToken: string, confirmed?: boolean) {
	const casinoId = env('CASINO_ID');
	console.log(accessToken);

	const body = {
		idToken: accessToken,
		socialPlatform: 'Facebook' as const,
		...(confirmed !== undefined ? { confirmed } : {}),
		...(await inviteCodeField()),
	};

	const res = await svaraApi.post('/api/svara/auth/socialLogin', body, {
		headers: {
			'Content-Type': 'application/json',
			'x-casino-context': JSON.stringify({ casinoId }),
		},
	});

	clearInviteCodeAfterSignIn(res.data);
	return res.data;
}

export const refreshTokenRequest = async (refresh_token: string) => {
	const response = await API.authRequest(
		'/refresh_token',
		'post',
		{ refresh_token },
		{
			'Content-Type': 'application/json',
			...API.getXCasinoHeader(),
			...API.getBearerHeader(),
		},
	);

	if (response.Error) throw response.Error;

	return response.Data?.data || response.Data;
};

export const logoutRequest = async (refresh_token: string) => {
	const response = await API.authRequest(
		'/logout',
		'post',
		{ refresh_token },
		{
			'Content-Type': 'application/json',
			...API.getXCasinoHeader(),
		},
	);

	if (response.Error) throw response.Error;

	return response.Data;
};

// ---------------------------------------------------------------------------
// Referrals (Invite Friends) - trinity-nest gateway-game `/referrals`
// ---------------------------------------------------------------------------

export type ReferralRewardStatus = 'LOCKED' | 'AVAILABLE' | 'CLAIMED';

export type ReferralInvitee = {
	playerId: string;
	username: string | null;
	avatarFileId: string | null;
	level: number;
	requiredLevel: number;
	isOnline: boolean;
	lastSeenAt: string | null;
	registeredAt: string;
	rewardStatus: ReferralRewardStatus;
	rewardClaimedAt: string | null;
};

export type ReferralOverview = {
	invitationCode: string | null;
	/** Null until the casino has an invite base URL configured; share the code instead. */
	referralLink: string | null;
	program: {
		isEnabled: boolean;
		requiredLevel: number;
		rewardAmount: number;
		maxRewards: number | null;
	};
	stats: { invitedCount: number; availableCount: number; claimedCount: number };
	totalCount: number;
	/** Claimable first, then recently active, then the rest, then already claimed. */
	items: ReferralInvitee[];
};

export type ReferralClaimResponse = {
	id: string;
	referredPlayerId: string;
	amount: number;
	transactionId: string;
	claimedAt: string;
};

export async function getReferralOverview(params?: {
	_start?: number;
	_end?: number;
}): Promise<ReferralOverview> {
	const response = await svaraApi.get('/api/referrals', { params });
	return response.data;
}

export async function claimReferralReward(
	referredPlayerId: string,
): Promise<ReferralClaimResponse> {
	const response = await svaraApi.post(
		`/api/referrals/${encodeURIComponent(referredPlayerId)}/claim`,
	);
	return response.data;
}
